import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { CallTypeStore } from '@/stores/CallTypeStore';
import { orderByField } from '@/utils/orderBy';
import type { ICallTypeInfo } from '~/api/ICallTypeInfo';
import type { ICallTypeMsg } from '~/api/messages/ICallTypeMsg';
import type { ICallTypeUpdateResMsg } from '~/api/messages/ICallTypeUpdateResMsg';

@SignalrListener
@service(CallTypeManager)
export class CallTypeManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(CallTypeStore) protected callTypeStore: CallTypeStore;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  find(id?: string) {
    return this.callTypeStore.find(id);
  }

  get all() {
    const callTypes = this.callTypeStore.all()
      .filter((callType) => callType.status !== STATUS.DISABLED);

    return orderByField(callTypes, this.callTypeStore.valueKey);
  }

  reset() {
    this.loaded = false;
    this.callTypeStore.set([]);
  }

  updateCallTypes(callTypes: ICallTypeInfo[]) {
    this.loaded = true;
    this.callTypeStore.batchUpdate(callTypes);
  }

  @SignalrMessage('CallTypeMsg')
  onCallTypeMsg(response: ICallTypeMsg) {
    this.updateCallTypes(response.callTypes);
  }

  @SignalrMessage('CallTypeUpdateMsg')
  onCallTypeUpdateMsg(response: ICallTypeMsg) {
    this.updateCallTypes(response.callTypes);
  }

  @SignalrMessage('CallTypeUpdateResMsg')
  onCallTypeUpdateResMsg(response: ICallTypeUpdateResMsg) {
    this.updateCallTypes([response.callType]);
  }

  getUnassignStatus(callType?: { id?: string } | null, status?: number | null) {
    const callTypeId = callType?.id;
    if (!callTypeId || status == null) {
      return false;
    }

    const callTypeInfo = this.find(callTypeId);
    const unassignUpTo = callTypeInfo ? Number(callTypeInfo.disableUnassignStatusId) : null;

    if (unassignUpTo === 0) {
      return true;
    }

    if (unassignUpTo != null && [2, 3, 4, 5, 6].includes(unassignUpTo)) {
      return status < unassignUpTo;
    }

    return false;
  }
}
